import { InputOTP6 } from "@/components/molecules/otp";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useEmailChallengeFlow } from "@/features/auth/hooks/use-email-challenge";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Input } from "../../../components/ui/input";
import type { OtherFactor } from "@/features/auth/types";

export const EmailChallenge = (props: {
  enforcedValue?: {
    label: string; // Ex : "r****m@wanadoo.fr"
    id: string; // Used by the backend to find the correct email
  };
  onSuccess: (
    preSignedToken: string,
    otherFactors: OtherFactor[],
    email: string,
    isNewEmail: boolean
  ) => void;
  loginMode?: boolean; // If true, if email is detected as first use, we'll show a message indicating that an account will be created
}) => {
  const { t } = useTranslation();
  const {
    email,
    setEmail,
    sendChallenge,
    codeSent,
    verifyCode,
    isVerifyingCode,
    retryTimeLeft,
    canRetry,
    retry,
    isNewEmail,
  } = useEmailChallengeFlow({
    enforcedValue: props.enforcedValue,
    onSuccess: props.onSuccess,
    retryDelaySeconds: 60,
  });
  const [isLoading, setIsLoading] = useState(false);

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <CardHeader>
          {!codeSent && (
            <CardDescription>{t("email.enterEmail")}</CardDescription>
          )}
          {codeSent && (
            <CardDescription>
              <Trans
                i18nKey="email.codeSent"
                values={{
                  email: props.enforcedValue?.label || email,
                }}
                components={{ strong: <strong /> }}
              />
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-6">
            {!codeSent && (
              <div className="grid gap-2">
                <Label>{t("email.label")}</Label>
                <Input
                  required
                  placeholder={t("email.placeholder")}
                  value={email}
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            )}
            {codeSent && (
              <>
                <div className="grid gap-2">
                  <Label>{t("otp.label")}</Label>
                  <InputOTP6
                    required
                    onChange={(value) => {
                      if (value.length === 6) {
                        verifyCode(value);
                      }
                    }}
                    disabled={isLoading}
                  />
                </div>

                {isNewEmail && props.loginMode && (
                  <p className="text-sm text-muted-foreground">
                    {t("email.newAccountNotice")}
                    <br />
                    <Button
                      variant="link"
                      className="p-0"
                      onClick={() => {
                        retry();
                      }}
                    >
                      {t("email.useAnotherEmail")}
                    </Button>
                  </p>
                )}
              </>
            )}
          </div>
        </CardContent>
        <CardFooter className="gap-2">
          {!codeSent && (
            <Button
              type="submit"
              className="w-full"
              onClick={async () => {
                setIsLoading(true);
                try {
                  await sendChallenge();
                } finally {
                  setIsLoading(false);
                }
              }}
              disabled={!email.length || isLoading || isVerifyingCode}
            >
              {isLoading ? t("actions.loading") : t("actions.sendCode")}
            </Button>
          )}

          {codeSent && !(isNewEmail && props.loginMode) && (
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={retry}
              disabled={!canRetry || isLoading || isVerifyingCode}
            >
              {canRetry
                ? t("actions.resendCode")
                : t("actions.retryIn", { seconds: retryTimeLeft })}
            </Button>
          )}
        </CardFooter>
      </form>
    </>
  );
};
